import PageLayout from "@/components/feature/PageLayout";
import { playbookResources } from "@/mocks/playbookResources";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { getPlaybookResources } from "@/services/content";

export default function Playbook() {
  // Falls back to src/mocks/playbookResources.ts until Supabase is wired up.
  const { data: resources } = useSupabaseData(getPlaybookResources, playbookResources);
  // Resources are open to everyone: each button opens the PDF in a new tab.
  const openResource = (url: string) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <PageLayout>
      {/* Header */}
      <section className="w-full px-4 md:px-8 lg:px-12 pt-28 md:pt-32 pb-8 bg-onyx-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-0.5 bg-strike-500"></div>
            <span className="font-body text-strike-500 text-xs font-semibold tracking-[0.2em] uppercase">
              Player Resources & Training Tools
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-white uppercase tracking-tight">
            Tools
          </h1>
          <p className="font-body text-white/50 text-sm md:text-base mt-3 max-w-xl">
            Downloadable guides and training film for players and parents.
            Study the system. Know the reads. Execute.
          </p>
        </div>
      </section>

      {/* PDF Downloads */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-8 bg-onyx-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Playbook PDF */}
            <div className="bg-gunmetal-50 border border-onyx-400 overflow-hidden hover:border-strike-500/40 transition-colors flex flex-col">
              <div className="relative aspect-[16/9] overflow-hidden group shrink-0">
                <img
                  src={resources.playbookPreviewImage}
                  alt="Triumph Playbook Preview"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-onyx-50/30 group-hover:bg-onyx-50/10 transition-colors" />
              </div>
              <div className="p-6 md:p-8 flex flex-col items-start flex-1">
                <h3 className="font-heading text-lg md:text-xl font-bold text-white uppercase tracking-wide mb-2">
                  Triumph Playbook
                </h3>
                <p className="font-body text-white/50 text-sm leading-relaxed mb-6">
                  Complete offensive and defensive system guide. Includes set
                  plays, motion offenses, defensive rotations, and special
                  situations for all age groups.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-auto w-full">
                  <button
                    onClick={() => openResource(resources.playbookPrintablePdfUrl)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-onyx-300 border border-onyx-400 text-white font-body text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap cursor-pointer hover:border-strike-500/50 transition-colors"
                  >
                    <i className="ri-printer-line"></i>
                    Printable
                  </button>
                  <button
                    onClick={() => openResource(resources.playbookPdfUrl)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-onyx-300 border border-onyx-400 text-white font-body text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap cursor-pointer hover:border-strike-500/50 transition-colors"
                  >
                    <i className="ri-smartphone-line"></i>
                    Digital
                  </button>
                </div>
              </div>
            </div>

            {/* Greenlight PDF */}
            <div className="bg-gunmetal-50 border border-onyx-400 overflow-hidden hover:border-strike-500/40 transition-colors flex flex-col">
              <div className="relative aspect-[16/9] overflow-hidden group shrink-0">
                <img
                  src={resources.greenlightPreviewImage}
                  alt="Greenlight Shooting Guide Preview"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-onyx-50/30 group-hover:bg-onyx-50/10 transition-colors" />
              </div>
              <div className="p-6 md:p-8 flex flex-col items-start flex-1">
                <h3 className="font-heading text-lg md:text-xl font-bold text-white uppercase tracking-wide mb-2">
                  Greenlight Shooting Guide
                </h3>
                <p className="font-body text-white/50 text-sm leading-relaxed mb-6">
                  Step-by-step shooting mechanics workbook. Form breakdown,
                  footwork diagrams, drill progressions, and daily training
                  templates for consistent improvement.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-auto w-full">
                  <button
                    onClick={() => openResource(resources.greenlightShootingPdfUrl)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-onyx-300 border border-onyx-400 text-white font-body text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap cursor-pointer hover:border-strike-500/50 transition-colors"
                  >
                    <i className="ri-printer-line"></i>
                    Printable
                  </button>
                  <button
                    onClick={() => openResource(resources.greenlightShootingPdfUrl)}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-onyx-300 border border-onyx-400 text-white font-body text-xs font-semibold uppercase tracking-wider px-4 py-3 whitespace-nowrap cursor-pointer hover:border-strike-500/50 transition-colors"
                  >
                    <i className="ri-smartphone-line"></i>
                    Digital
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Library */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-16 md:pb-24 bg-onyx-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-0.5 bg-strike-500"></div>
            <span className="font-body text-strike-500 text-xs font-semibold tracking-[0.2em] uppercase">
              Training Film
            </span>
          </div>
          <p className="font-body text-white/50 text-sm md:text-base mb-8 max-w-2xl">
            These are some of our favorite videos online that can help you improve quickly.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {resources.youtubeVideos.map((video, idx) => (
              <div
                key={idx}
                className="bg-gunmetal-50 border border-onyx-400 overflow-hidden hover:border-strike-500/40 transition-colors flex flex-col"
              >
                <a
                  href={video.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block relative aspect-video overflow-hidden group shrink-0"
                >
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-onyx-50/40 group-hover:bg-onyx-50/20 transition-colors flex items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center bg-strike-500">
                      <i className={`${video.isExternal ? "ri-external-link-line" : "ri-play-fill"} text-onyx-50 text-xl`}></i>
                    </div>
                  </div>
                </a>
                <div className="p-4 flex-1">
                  <h4 className="font-body text-white text-sm font-medium leading-snug uppercase">
                    {video.title}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}